/**
 * Conjunto de focos pronto para desenhar: junta as fontes ligadas, aplica os
 * filtros (Amazônia Legal, satélites), elimina repetições entre fontes e
 * ordena por data. Recalculado só quando dados ou filtros mudam — nunca a
 * cada quadro do loop.
 *
 * O GOES-19 aparece nas duas fontes com os mesmos focos (mesma lat/lon/data).
 * Com as duas ligadas, fica o do 10 min (cor do GOES-19), completado com os
 * atributos do diário (município, bioma, FRP…) para o popup.
 */
import { fireKey } from './csv';
import { titleCasePt, ufSigla } from './format';
import { frameStep, indexRange, loopPeriod, visibleWindow, type LoopSnapshot } from './timeFilter';
import type { DisplayMode, Fire, FireSource, FireTable } from './types';

export const GOES_SATELLITE = 'GOES-19';

/** Satélites que o INPE usa hoje (para ler links antigos com lista de permitidos). */
export const KNOWN_SATELLITES = [
  'GOES-19', 'AQUA_M-T', 'AQUA_M-M', 'TERRA_M-T', 'TERRA_M-M', 'NOAA-20', 'NOAA-21',
  'NPP-375', 'NPP-375D', 'MSG-03', 'METOP-B', 'METOP-C',
] as const;
/** Classe visual: 0 = GOES-19, 1 = outros satélites. */
export const CLASS_GOES = 0;
export const CLASS_OTHERS = 1;

export interface DatasetFilters {
  sources: Record<FireSource, boolean>;
  onlyAmazonia: boolean;
  /** Satélites ocultos (vazio = todos visíveis; satélites novos aparecem). */
  hidden: ReadonlySet<string>;
}

export interface DatasetInput {
  goes10min: FireTable | null;
  diario: readonly FireTable[];
}

export interface FireDataset {
  n: number;
  x: Float64Array;
  y: Float64Array;
  /** Crescente. */
  t: Float64Array;
  cls: Uint8Array;
  /** Tabela e linha de origem (para o popup). */
  tbl: Uint16Array;
  row: Uint32Array;
  /** Linha do diário que completa um foco de 10 min (tabela −1 = nenhuma). */
  enrichTbl: Int16Array;
  enrichRow: Uint32Array;
  tables: FireTable[];
  /** goesPrefix[i] = nº de focos GOES-19 em [0, i). */
  goesPrefix: Uint32Array;
  version: number;
}

let versionSeq = 0;

export function emptyDataset(): FireDataset {
  return {
    n: 0,
    x: new Float64Array(0),
    y: new Float64Array(0),
    t: new Float64Array(0),
    cls: new Uint8Array(0),
    tbl: new Uint16Array(0),
    row: new Uint32Array(0),
    enrichTbl: new Int16Array(0),
    enrichRow: new Uint32Array(0),
    tables: [],
    goesPrefix: new Uint32Array(1),
    version: ++versionSeq,
  };
}

interface Pick {
  tbl: number;
  row: number;
  t: number;
  enrichTbl: number;
  enrichRow: number;
}

export function buildDataset(input: DatasetInput, filters: DatasetFilters): FireDataset {
  const tables: FireTable[] = [];
  const ten = filters.sources.goes10min ? input.goes10min : null;
  const daily = filters.sources.diario ? input.diario : [];
  const hidden = filters.hidden;
  const satOk = (name: string) => !hidden.has(name);
  const finite = (tb: FireTable, r: number) => Number.isFinite(tb.x[r]) && Number.isFinite(tb.y[r]);

  // Focos de 10 min por chave, para achar os mesmos focos no diário.
  const tenIndex = ten ? tables.push(ten) - 1 : -1;
  const tenKeys = new Map<string, number>();
  if (ten && daily.length) {
    for (let r = 0; r < ten.length; r++) tenKeys.set(fireKey(ten.lat[r]!, ten.lon[r]!, ten.t[r]!), r);
  }
  const enrich = new Map<number, [number, number]>();

  const dailyPicks: Pick[] = [];
  for (const tb of daily) {
    const ti = tables.push(tb) - 1;
    const goesIdx = tb.dict.sat.indexOf(GOES_SATELLITE);
    for (let r = 0; r < tb.length; r++) {
      if (goesIdx >= 0 && tb.sat[r] === goesIdx && tenKeys.size) {
        const k = tenKeys.get(fireKey(tb.lat[r]!, tb.lon[r]!, tb.t[r]!));
        if (k !== undefined) {
          enrich.set(k, [ti, r]);
          continue;
        }
      }
      if (filters.onlyAmazonia && !tb.amz[r]) continue;
      if (!satOk(tb.dict.sat[tb.sat[r]!]!)) continue;
      if (!finite(tb, r)) continue;
      dailyPicks.push({ tbl: ti, row: r, t: tb.t[r]!, enrichTbl: -1, enrichRow: 0 });
    }
  }

  const tenPicks: Pick[] = [];
  if (ten) {
    for (let r = 0; r < ten.length; r++) {
      const e = enrich.get(r);
      // Com correspondência no diário, vale o filtro por município (mais preciso).
      const amz = e ? tables[e[0]]!.amz[e[1]] : ten.amz[r];
      if (filters.onlyAmazonia && !amz) continue;
      if (!satOk(ten.dict.sat[ten.sat[r]!]!)) continue;
      if (!finite(ten, r)) continue;
      tenPicks.push({ tbl: tenIndex, row: r, t: ten.t[r]!, enrichTbl: e ? e[0] : -1, enrichRow: e ? e[1] : 0 });
    }
  }

  const sortedByT = (a: Pick[]) => {
    for (let i = 1; i < a.length; i++) if (a[i]!.t < a[i - 1]!.t) return a.sort((p, q) => p.t - q.t);
    return a;
  };
  const A = sortedByT(tenPicks);
  const B = sortedByT(dailyPicks);

  const n = A.length + B.length;
  const ds = emptyDataset();
  ds.n = n;
  ds.x = new Float64Array(n);
  ds.y = new Float64Array(n);
  ds.t = new Float64Array(n);
  ds.cls = new Uint8Array(n);
  ds.tbl = new Uint16Array(n);
  ds.row = new Uint32Array(n);
  ds.enrichTbl = new Int16Array(n);
  ds.enrichRow = new Uint32Array(n);
  ds.goesPrefix = new Uint32Array(n + 1);
  ds.tables = tables;

  let i = 0, a = 0, b = 0;
  while (a < A.length || b < B.length) {
    // Em empate, o diário (outros satélites) vem antes: o GOES-19 fica por cima.
    const p = b < B.length && (a >= A.length || B[b]!.t <= A[a]!.t) ? B[b++]! : A[a++]!;
    const tb = tables[p.tbl]!;
    ds.x[i] = tb.x[p.row]!;
    ds.y[i] = tb.y[p.row]!;
    ds.t[i] = p.t;
    const isGoes = tb.dict.sat[tb.sat[p.row]!] === GOES_SATELLITE;
    ds.cls[i] = isGoes ? CLASS_GOES : CLASS_OTHERS;
    ds.tbl[i] = p.tbl;
    ds.row[i] = p.row;
    ds.enrichTbl[i] = p.enrichTbl;
    ds.enrichRow[i] = p.enrichRow;
    ds.goesPrefix[i + 1] = ds.goesPrefix[i]! + (isGoes ? 1 : 0);
    i++;
  }
  return ds;
}

/**
 * Subconjunto do conjunto com os índices dados (crescentes: a ordem por data
 * se mantém). Usado pelo filtro por áreas: o mapa, os contadores e a lista
 * recebem só os focos das áreas, sem mudar o desenho nem as buscas binárias.
 */
export function subsetDataset(src: FireDataset, idx: ArrayLike<number>): FireDataset {
  const n = idx.length;
  const ds = emptyDataset();
  ds.n = n;
  ds.x = new Float64Array(n);
  ds.y = new Float64Array(n);
  ds.t = new Float64Array(n);
  ds.cls = new Uint8Array(n);
  ds.tbl = new Uint16Array(n);
  ds.row = new Uint32Array(n);
  ds.enrichTbl = new Int16Array(n);
  ds.enrichRow = new Uint32Array(n);
  ds.goesPrefix = new Uint32Array(n + 1);
  ds.tables = src.tables;
  for (let k = 0; k < n; k++) {
    const i = idx[k]!;
    ds.x[k] = src.x[i]!;
    ds.y[k] = src.y[i]!;
    ds.t[k] = src.t[i]!;
    ds.cls[k] = src.cls[i]!;
    ds.tbl[k] = src.tbl[i]!;
    ds.row[k] = src.row[i]!;
    ds.enrichTbl[k] = src.enrichTbl[i]!;
    ds.enrichRow[k] = src.enrichRow[i]!;
    ds.goesPrefix[k + 1] = ds.goesPrefix[k]! + (src.cls[i] === CLASS_GOES ? 1 : 0);
  }
  return ds;
}

export interface WindowCount {
  total: number;
  goes: number;
  others: number;
}

/** Contagem de focos com from < t ≤ to, por classe (O(log n)). */
export function countWindow(ds: FireDataset, from: number, to: number): WindowCount {
  const [lo, hi] = indexRange(ds.t, from, to);
  const goes = ds.goesPrefix[hi]! - ds.goesPrefix[lo]!;
  return { total: hi - lo, goes, others: hi - lo - goes };
}

export interface FireCounts {
  /** Focos do quadro atual: (atual − passo, atual]. */
  frame: WindowCount;
  /** Focos do período do loop (união dos intervalos dos quadros). */
  period: WindowCount;
  /** Focos visíveis no modo atual (no acumulado: do início do período até o quadro atual). */
  shown: WindowCount;
  /** Instante do quadro atual. */
  now: number;
}

/**
 * Contagens do painel, todas a partir do MESMO snapshot (dados + loop), sem
 * estados intermediários. Invariantes: soma dos quadros = período; acumulado
 * no 1º quadro = quadro; acumulado no último quadro = período.
 */
export function computeCounts(ds: FireDataset, mode: DisplayMode, t: LoopSnapshot): FireCounts | null {
  const period = loopPeriod(t.frames, t.range);
  if (!period) return null;
  const step = frameStep(t.frames, t.index);
  const now = t.currentTime ?? period.to;
  const w = visibleWindow(mode, t.currentTime, step, period)!;
  return {
    frame: countWindow(ds, now - step, now),
    period: countWindow(ds, period.from, period.to),
    shown: countWindow(ds, w.from, w.to),
    now,
  };
}

/** Satélites presentes nos dados carregados (independe dos filtros). */
export function listSatellites(input: DatasetInput): string[] {
  const set = new Set<string>();
  if (input.goes10min) input.goes10min.dict.sat.forEach((s) => set.add(s));
  for (const tb of input.diario) {
    const used = new Uint8Array(tb.dict.sat.length);
    for (let r = 0; r < tb.length; r++) used[tb.sat[r]!] = 1;
    tb.dict.sat.forEach((s, k) => used[k] && set.add(s));
  }
  return sortSatellites([...set]);
}

/** Ordem de exibição: GOES-19, referência (AQUA_M-T), demais em ordem alfabética. */
export function sortSatellites(names: string[]): string[] {
  const rank = (s: string) => (s === GOES_SATELLITE ? 0 : s === 'AQUA_M-T' ? 1 : 2);
  return [...names].sort((a, b) => rank(a) - rank(b) || a.localeCompare(b));
}

const nullIfNaN = (v: number | undefined) => (v === undefined || Number.isNaN(v) ? null : v);

/** Monta o objeto `Fire` do índice `i` do conjunto (para o popup). */
export function materializeFire(ds: FireDataset, i: number): Fire {
  const tb = ds.tables[ds.tbl[i]!]!;
  const r = ds.row[i]!;
  const fire: Fire = {
    id: tb.extra?.ids[r] ?? fireKey(tb.lat[r]!, tb.lon[r]!, tb.t[r]!),
    lat: tb.lat[r]!,
    lon: tb.lon[r]!,
    t: tb.t[r]!,
    satelite: tb.dict.sat[tb.sat[r]!] ?? '?',
    source: tb.source,
  };
  const et = ds.enrichTbl[i]!;
  const [src, sr] = tb.extra ? [tb, r] : et >= 0 ? [ds.tables[et]!, ds.enrichRow[i]!] : [null, 0];
  const ex = src?.extra;
  if (ex) {
    const mun = ex.munNames[ex.mun[sr]!];
    const bioma = ex.biomaNames[ex.bioma[sr]!];
    fire.municipio = mun ? titleCasePt(mun) : null;
    fire.estado = ufSigla(ex.uf[sr]);
    fire.cdMun = ex.cdMun[sr] ? String(ex.cdMun[sr]) : null;
    fire.diasSemChuva = nullIfNaN(ex.dias[sr]);
    fire.precipitacao = nullIfNaN(ex.precip[sr]);
    fire.riscoFogo = nullIfNaN(ex.risco[sr]);
    fire.bioma = bioma || null;
    fire.frp = nullIfNaN(ex.frp[sr]);
  }
  return fire;
}
